import { type ReactNode, useState } from 'react';
import { ToggleButton } from 'react-aria-components';
import { useFetcher, useNavigate } from 'react-router';

import type { AddFavoriteRequestV1, AddFavoriteResponseV1 } from '~/api/v1/mall/add-favorite';
import type { FavoriteCampaignInfoV1 } from '~/api/v1/mall/favorite-campaign';
import type { RemoveFavoriteRequestV1, RemoveFavoriteResponseV1 } from '~/api/v1/mall/remove-favorite';
import type { RecommendShopItemV2 } from '~/api/v2/mall/recommend-shop';
import type { ShopDetailResponseV2 } from '~/api/v2/mall/shop/campaign-id';
import type { CampaignInfoV2 } from '~/api/v2/schemas/campaign-info';

type FavoriteActionRequestBody = {
  campaignId: number;
  favorite: boolean;
};

type MakeFavoriteActionArgs = {
  addFavoriteApi: (req: AddFavoriteRequestV1) => Promise<AddFavoriteResponseV1>;
  removeFavoriteApi: (req: RemoveFavoriteRequestV1) => Promise<RemoveFavoriteResponseV1>;
};

type FavoriteAction = (request: Request) => Promise<void>;
type MakeFavoriteAction = (args: MakeFavoriteActionArgs) => FavoriteAction;

export const makeFavoriteAction: MakeFavoriteAction =
  ({ addFavoriteApi, removeFavoriteApi }) =>
  async (request) => {
    const { campaignId, favorite }: FavoriteActionRequestBody = await request.json();
    console.log({ campaignId, favorite });

    if (favorite) {
      await addFavoriteApi({ campaign_id: campaignId });
    } else {
      await removeFavoriteApi({ campaign_id: campaignId });
    }
  };

const FAVORITE_ACTION_ROUTE: string = import.meta.env.VITE_FAVORITE_ACTION_ROUTE;

type FavoriteRenderArgs = { isFavorite: boolean };

type PropsCommon = {
  className?: string;
  children: (args: FavoriteRenderArgs) => ReactNode;
  isLoggedIn: boolean;
  redirectToLogin: string;
};

export type FavoriteProps =
  | (PropsCommon & { campaignInfo: CampaignInfoV2 })
  | (PropsCommon & { recommendShopDetail: RecommendShopItemV2 })
  | (PropsCommon & { favoriteCampaign: FavoriteCampaignInfoV1 })
  | (PropsCommon & { shopDetail: ShopDetailResponseV2 });

export const Favorite = (props: FavoriteProps) => {
  const { className, children, isLoggedIn, redirectToLogin } = props;
  const navigate = useNavigate();
  // Extract necessary properties
  let campaignId: number;
  let shouldShowFavorite: boolean;
  let initialState: boolean;

  if ('campaignInfo' in props) {
    campaignId = props.campaignInfo.campaign_id;
    shouldShowFavorite = props.campaignInfo.reward_type === 1;
    initialState = props.campaignInfo.favorite_flg;
  } else if ('recommendShopDetail' in props) {
    campaignId = props.recommendShopDetail.campaign_id;
    shouldShowFavorite = false;
    initialState = false;
  } else if ('favoriteCampaign' in props) {
    campaignId = props.favoriteCampaign.campaign_id;
    shouldShowFavorite = props.favoriteCampaign.reward_type === 1;
    // favoriteCampaignで渡ってくるものは、内部的にはfavorite_flgがtrueである
    initialState = true;
  } else if ('shopDetail' in props) {
    campaignId = props.shopDetail.campaign_id;
    shouldShowFavorite = props.shopDetail.reward_type === 1;
    initialState = props.shopDetail.favorite_flg;
  } else {
    throw new Error('unreachable');
  }

  // Check if favorite should be shown
  if (!shouldShowFavorite) {
    return null;
  }

  const [favorite, setFavorite] = useState(initialState);
  const fetcher = useFetcher();

  return (
    <ToggleButton
      defaultSelected={favorite}
      onChange={(isSelected) => {
        if (!isLoggedIn) {
          navigate(`/login?redirect=${redirectToLogin}`);
          return;
        }
        setFavorite(isSelected);
        const data: FavoriteActionRequestBody = { campaignId, favorite: isSelected };
        fetcher.submit(data, { method: 'POST', action: FAVORITE_ACTION_ROUTE, encType: 'application/json' });
      }}
      onClick={(e) => {
        // Eventの伝播を停止
        e.stopPropagation();
        // Link要素の中に置かれた場合等に、Linkの動作をキャンセル
        e.preventDefault();
      }}
      className={className}
      aria-label="favorite">
      {children({ isFavorite: favorite })}
    </ToggleButton>
  );
};
